import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { CommonRootState } from '../forces/root.reducer';
import { authService } from '../utils/auth/AuthService';
import { useFeatureFlag } from '../utils/featureFlags';
import { getLogger } from '../utils/logger/logger';

import { rememberInactivityReload, reportInactivityReload } from './inactivityReloadTracking';
import { reloadWhenReachable } from './reloadWhenReachable';
import { isPlaybackActive, useInactivityMonitor } from './useInactivityMonitor';

const logger = getLogger('[auto-reload]');

export const useAutoReloadStaleApp = () => {
  const location = useLocation();
  const isInternalUser = authService.isInternalUser();
  const reloadOnNewVersion = isInternalUser;
  const [staleRevalidationEnabled] = useFeatureFlag('enable-stale-revalidation');

  const newVersionAvailable = useSelector((state: CommonRootState) => state.ui.newVersionReleased);
  const { longInactivity } = useInactivityMonitor(isInternalUser || Boolean(staleRevalidationEnabled));

  useEffect(() => {
    reportInactivityReload();
  }, []);

  useEffect(() => {
    logger.log({ newVersionAvailable });

    if (reloadOnNewVersion && newVersionAvailable) {
      // Trigger full refresh on navigation when new version has been released
      logger.info('full refresh on navigation since new version released', { location });
      window.location.reload();
    }
  }, [newVersionAvailable, location, reloadOnNewVersion]);

  useEffect(() => {
    if (!longInactivity) {
      return;
    }
    // Trigger full refresh when user opens tab again after long inactivity
    logger.info('full refresh after long inactivity', { location: window.location.href });
    return reloadWhenReachable(failedChecks => {
      // The reachability check can retry for a while, long enough for the user to start watching
      if (longInactivity.reason === 'away' && isPlaybackActive()) {
        logger.info('playback started before the app was reachable, not reloading');
        return;
      }
      rememberInactivityReload({ ...longInactivity, reachabilityRetries: failedChecks });
      window.location.reload();
    });
  }, [longInactivity]);
};
